import { useState } from 'react';
import { NavigateFunction } from 'react-router-dom';

import { PublicImageItem } from '~/Nova/scenes/HostingList/utils/types';

import { REIMAGE_ROUTE_TO_TAB_PATH, ReimageTab } from '../utils/constants';
import {
  filterApplicationImages,
  filterGroupedOsImages,
} from '../utils/searchHelpers';

const REIMAGE_TABS = Object.keys(REIMAGE_ROUTE_TO_TAB_PATH) as ReimageTab[];

interface UseReimageSearchProps {
  pathname: string;
  navigate: NavigateFunction;
  groupedOsImages: Record<string, PublicImageItem[]>;
  applicationImages: PublicImageItem[];
}

export const useReimageSearch = ({
  pathname,
  navigate,
  groupedOsImages,
  applicationImages,
}: UseReimageSearchProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const normalizedQuery = searchQuery.trim().toLowerCase();

  // Compute filtered results based on the search query and the provided image lists.
  const os = filterGroupedOsImages(groupedOsImages, normalizedQuery);
  const applications = filterApplicationImages(
    applicationImages,
    normalizedQuery,
  );
  const hasResults = {
    'plain-os': Object.keys(os).length > 0,
    applications: applications.length > 0,
  } satisfies Record<ReimageTab, boolean>;

  const onSearchChange = (
    value: string,
    currentTab: ReimageTab | null = null,
  ) => {
    setSearchQuery(value);
    if (!currentTab) {
      return;
    }
    const query = value.trim().toLowerCase();
    if (!query) {
      return;
    }
    // Use the already-computed results if the normalized query hasn't changed.
    const currentResults =
      query === normalizedQuery
        ? hasResults
        : {
            'plain-os':
              Object.keys(filterGroupedOsImages(groupedOsImages, query))
                .length > 0,
            applications:
              filterApplicationImages(applicationImages, query).length > 0,
          };
    if (currentResults[currentTab]) {
      return;
    }
    const nextTab = REIMAGE_TABS.find((tab) => currentResults[tab]);
    if (!nextTab) {
      return;
    }
    const marker = '/reimage-server/';
    const index = pathname.indexOf(marker);
    const basePath = index === -1 ? pathname : pathname.slice(0, index);
    navigate(`${basePath}/${REIMAGE_ROUTE_TO_TAB_PATH[nextTab]}`);
  };
  return {
    searchQuery,
    onSearchChange,
    filteredOsImages: os,
    filteredApplicationImages: applications,
  };
};
